import type { ReactNode } from 'react';
import { useCallback } from 'react';

import type { NativeReportColumn, NativeReportResponse } from '@/api/reports';
import { Button } from '@/lib/base/buttons/button';
import type { ChartDatapointSelection } from '@/types/reports';
import { cx } from '@/utils/cx';

import {
  compactNumberFormatter,
  formatMeasureValue,
  formatXLabel,
  getCompactCurrencyFormatter,
  getCurrencyFormatter,
} from '../constants';
import { zeroIsMeaningful } from '../utils/metricExport';
import { isAggregationColumn } from '../utils/nativeDataTransforms';

/**
 * Magnitude at or above which the headline renders compacted ($6.9M rather
 * than $6,871,240). Below it the exact figure already fits, and a KPI reads
 * better precise than rounded.
 */
const COMPACT_THRESHOLD = 10_000;

/** Shown where there is no number to show — the table empty-value glyph. */
const EMPTY_VALUE = '—';

/**
 * Focus mode / live preview: the number is the whole surface, so it sits
 * centred in whatever height it is given.
 */
const ROOT_FULL_CLASSES =
  'flex h-full min-h-[112px] flex-col items-center justify-center gap-1 text-center';
/**
 * Dashboard tile: a stat card — number and label ranged left under the
 * title band, only as tall as they need to be. The tile's row is sized by
 * its content, so this is what decides the KPI row's height. The tile body
 * gives a metric no padding of its own (so the hover tint can reach the
 * card edges); the padding lives here instead.
 */
const ROOT_COMPACT_CLASSES =
  'flex h-full min-h-[84px] flex-col items-start justify-center gap-1 text-left';
/** `sm:` is spelled out because Button carries a responsive `sm:py-*` of its
 *  own that a bare `py-3` would not replace. */
const COMPACT_PADDING_CLASSES = 'px-4 py-3 sm:py-3';

const VALUE_BASE_CLASSES =
  'font-semibold tabular-nums tracking-tight text-fg-primary';
/** Focus mode / live preview — the number is the whole surface. */
const VALUE_FULL_CLASSES = 'text-display-md lg:text-display-lg';
/** Dashboard tile — a quarter-width card, so one step down. */
const VALUE_COMPACT_CLASSES = 'text-display-sm lg:text-display-md';

/** The measure's own name, a caption under the number — medium so it holds
 *  its own beside a display-weight figure without competing with it. */
const LABEL_CLASSES = 'text-sm font-medium text-fg-tertiary';
const SUB_LABEL_CLASSES = 'text-xs text-fg-quaternary';
const MIXED_HEADLINE_CLASSES = 'text-sm font-medium text-fg-secondary';
const CURRENCY_LINE_CLASSES =
  'text-lg font-semibold tabular-nums text-fg-primary';

/**
 * The whole tile is the drill target, so the button has to fill it rather
 * than sit inside it. `tertiary` is the Button colour that only tints on
 * hover — `link-gray` underlines its text, which on a headline figure reads
 * as a hyperlink. `cx` (tailwind-merge) lets these classes win over Button's
 * own inline-flex / h-max / padding so the tint reaches the tile's edges.
 */
const BUTTON_BASE_CLASSES =
  'flex h-full w-full flex-col justify-center gap-1 whitespace-normal';
const BUTTON_FULL_CLASSES = cx(
  BUTTON_BASE_CLASSES,
  'items-center rounded-lg text-center'
);
const BUTTON_COMPACT_CLASSES = cx(
  BUTTON_BASE_CLASSES,
  COMPACT_PADDING_CLASSES,
  'items-start rounded-none text-left'
);
/** Value over label, the same stack the inert rendering gets from its root. */
const STACK_FULL_CLASSES = 'flex flex-col items-center gap-1';
const STACK_COMPACT_CLASSES = 'flex flex-col items-start gap-1';

/** One currency's subtotal in an unconverted, multi-currency aggregate. */
interface CurrencyTotal {
  amount: number;
  unit: string;
}

/**
 * A multi-currency aggregate with no `convert_to` arrives as a list of
 * per-unit subtotals rather than a scalar — the backend deliberately refuses
 * to sum across units (issue #13623). Anything that isn't a finite amount is
 * dropped so a malformed entry can never surface as "NaN".
 */
function toCurrencyTotals(value: unknown): CurrencyTotal[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const totals: CurrencyTotal[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) {
      continue;
    }
    const { amount, unit } = entry as { amount?: unknown; unit?: unknown };
    const numeric = typeof amount === 'number' ? amount : Number(amount);
    if (!Number.isFinite(numeric) || typeof unit !== 'string' || !unit) {
      continue;
    }
    totals.push({ amount: numeric, unit });
  }
  return totals;
}

/**
 * Headline text plus the exact figure for the `title` tooltip. They differ
 * only once compacting kicks in, so the precise number is always one hover
 * away.
 *
 * Percentages never compact: they are stored as fractions, so the threshold
 * can only be crossed by a value that is already nonsensical as a percentage.
 */
function formatHeadline(
  value: number,
  column: NativeReportColumn,
  currency: string
): { display: string; exact: string } {
  const exact = formatMeasureValue(value, column.key, currency, column.type);
  if (column.type === 'percentage' || Math.abs(value) < COMPACT_THRESHOLD) {
    return { display: exact, exact };
  }
  const display =
    column.type === 'currency'
      ? getCompactCurrencyFormatter(currency).format(value)
      : compactNumberFormatter.format(value);
  return { display, exact };
}

/**
 * The number to render, or `null` when there genuinely isn't one.
 *
 * `undefined` means the query matched nothing — the aggregation columns are
 * present even when `rows` is empty, so the column is always available to
 * decide whether that means 0 or "no value".
 */
function resolveNumericValue(
  rawValue: unknown,
  column: NativeReportColumn
): number | null {
  if (rawValue === undefined || rawValue === null) {
    return zeroIsMeaningful(column) ? 0 : null;
  }
  const candidate = typeof rawValue === 'number' ? rawValue : Number(rawValue);
  return Number.isFinite(candidate) ? candidate : null;
}

export interface MetricChartCaseProps {
  nativeData: NativeReportResponse;
  /** ISO 4217 code used to format a currency-typed measure. */
  currency?: string;
  /** Primary dimension name, used only to label a defensive group sub-label. */
  dimensionName?: string;
  /** Tile-scale rendering. Focus mode and the live preview pass `false`. */
  compact?: boolean;
  /** Supplied only where the metric can be drilled into; omit for read-only. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
}

/**
 * Single-value KPI renderer — one big number with the measure's own label
 * under it.
 *
 * Reached from `ChartRendererCore` before the chart transforms run, because an
 * ungrouped query has no dimension column for `nativeToChartData` to pivot on
 * and would otherwise fall through to the "no data" empty state.
 *
 * Loading, error, and no-query states belong to `ChartRendererCore` and
 * `DashboardReportCard`; this renders a resolved result only.
 */
export function MetricChartCase({
  nativeData,
  currency = 'USD',
  dimensionName,
  compact = false,
  onDatapointSelect,
}: MetricChartCaseProps) {
  const measureColumn = nativeData.columns.find(isAggregationColumn);
  const measureKey = measureColumn?.key;

  const handleSelect = useCallback(() => {
    if (!measureKey || !onDatapointSelect) {
      return;
    }
    onDatapointSelect({ rowIndex: 0, seriesKey: measureKey });
  }, [measureKey, onDatapointSelect]);

  const valueClasses = cx(
    VALUE_BASE_CLASSES,
    compact ? VALUE_COMPACT_CLASSES : VALUE_FULL_CLASSES
  );
  const rootClasses = compact ? ROOT_COMPACT_CLASSES : ROOT_FULL_CLASSES;
  const buttonClasses = compact ? BUTTON_COMPACT_CLASSES : BUTTON_FULL_CLASSES;
  const stackClasses = compact ? STACK_COMPACT_CLASSES : STACK_FULL_CLASSES;

  let body: ReactNode;

  if (!measureColumn) {
    // No aggregation column at all — nothing this renderer can show.
    body = <span className={valueClasses}>{EMPTY_VALUE}</span>;
  } else {
    const row = nativeData.rows[0];
    const rawValue = row?.[measureColumn.key];
    const currencyTotals = toCurrencyTotals(rawValue);

    if (currencyTotals.length > 0) {
      body = (
        <>
          <span className={MIXED_HEADLINE_CLASSES}>Mixed currencies</span>
          {/* A span, not a div: when the metric is drillable this whole body
              renders inside a Button, whose content model is phrasing only. */}
          <span className='flex flex-col gap-0.5'>
            {currencyTotals.map(total => (
              <span key={total.unit} className={CURRENCY_LINE_CLASSES}>
                {getCurrencyFormatter(total.unit).format(total.amount)}
              </span>
            ))}
          </span>
          <span className={LABEL_CLASSES}>{measureColumn.label}</span>
        </>
      );
    } else {
      const numericValue = resolveNumericValue(rawValue, measureColumn);

      if (numericValue === null) {
        body = (
          <>
            <span className={valueClasses}>{EMPTY_VALUE}</span>
            <span className={LABEL_CLASSES}>{measureColumn.label}</span>
          </>
        );
      } else {
        const { display, exact } = formatHeadline(
          numericValue,
          measureColumn,
          currency
        );
        // Only a grouped query has a dimension column; an ungrouped metric has
        // none, so this sub-label is defensive — it names which bucket the one
        // rendered row belongs to if a grouped query ever reaches here.
        const groupColumn = nativeData.columns.find(
          col => !isAggregationColumn(col)
        );
        const groupLabel =
          groupColumn && row
            ? formatXLabel(row[groupColumn.key], dimensionName)
            : null;

        body = (
          <>
            <span className={valueClasses} title={exact}>
              {display}
            </span>
            <span className={LABEL_CLASSES}>{measureColumn.label}</span>
            {groupLabel ? (
              <span className={SUB_LABEL_CLASSES}>{groupLabel}</span>
            ) : null}
          </>
        );
      }
    }
  }

  // Inert without a drill handler, and inert with no rows — a zero-row metric
  // reads "0", and there are no records behind it to open.
  if (!onDatapointSelect || !measureColumn || nativeData.rows.length === 0) {
    return (
      <div
        className={cx(rootClasses, compact ? COMPACT_PADDING_CLASSES : null)}
      >
        {body}
      </div>
    );
  }

  return (
    <div className={rootClasses}>
      <Button
        color='tertiary'
        size='sm'
        noTextPadding
        className={buttonClasses}
        onClick={handleSelect}
      >
        {/* Button lays its children out inline, which would put the label on
            the same line as the number; this stack keeps the label under it,
            matching the inert (non-drillable) rendering above. */}
        <span className={stackClasses}>{body}</span>
      </Button>
    </div>
  );
}
